// ============================================
// Gallery System
// Replaces GalleryOverlayController.cs
// Virtual scrolling via IntersectionObserver
// ============================================

import { events, Events } from '../engine/events.js';
import { IMAGE_FOLDERS } from '../engine/constants.js';
import { getImageManifest, getImageCache, getImageMissCache } from './story-content.js';
import { isTarMode, getImageUrl } from '../image-loader.js';
import { L } from '../systems/localization.js';
import { appearanceState } from '../systems/appearance.js';
import { audioManager } from '../systems/audio.js';
import { createIsolatedClip, isVoiceClip, releaseClipsIn } from './components/isolated-video.js';

// Audit D17: per-category empty-state hints. Each entry hints at the kind of
// content the category holds without spoiling individual scenes. Fallback
// hint is generic enough to fit any uncovered category.
// Localized via L.get('gallery.empty.<cat>.title' / '.body') with EN fallback.
function _galleryEmptyHint(category) {
    const EN_FALLBACK = {
        'All':           { title: 'No images unlocked yet', body: 'Story scenes you see in-game land here automatically. Play through a few beats and check back.' },
        '__videos__':    { title: 'No videos unlocked yet', body: 'Animated scenes (mirror-clips, stream highlights, late-Master beats) appear here when you reach them in the story.' },
        'Apartment':     { title: 'Nothing from home yet', body: 'Apartment scenes unlock as you set up your room, sleep, shower, or use the mirror at different Fem tiers.' },
        'Bar':           { title: 'No bar scenes yet', body: 'Work a shift, take an order, follow someone into the back. The bar fills up fast once you start working.' },
        'Bedroom':       { title: 'No bedroom scenes yet', body: 'Sarah-visits, Master encounters, and solo intimate beats end up here once you reach them.' },
        'Park':          { title: 'Nothing from the park yet', body: 'Walks, late-night detours, and the things that find you in the trees all live here.' },
        'Mall':          { title: 'No mall scenes yet', body: 'Shopping trips and what happens in the changing rooms. Bring money.' },
        'Beach':         { title: 'No beach scenes yet', body: 'Day-trips, lifeguard hut, evening crowd. You need to actually visit the beach for any of this to fire.' },
        'Streaming':     { title: 'No stream scenes yet', body: 'Buy a webcam, set up the room, go live. Stream scenes and chat-rooms unlock as you build an audience.' },
        'Master':        { title: 'No Master scenes yet', body: 'Master scenes unlock as your relationship deepens. Independent runs see different content entirely.' },
        'Sarah':         { title: 'No girlfriend scenes yet', body: 'Sarah arrives Saturday nights. Her arc evolves based on what she finds out about you.' },
        'Gym':           { title: 'No gym scenes yet', body: 'Workouts, sauna run-ins, and what happens after a shower. Visit the gym to fill this in.' },
        'Salon':         { title: 'No salon scenes yet', body: 'Manicures, waxing, hair appointments. Each treatment has its own scene and aftermath.' },
        'Clinic':        { title: 'No clinic scenes yet', body: 'HRT consults, Dr. Mercer, treatment-room beats. Unlocks once you start the medical path.' },
        'Tattoo':        { title: 'No tattoo scenes yet', body: 'Inkwell Studio scenes appear here once you sit in the chair.' },
    };
    const slug = (category || '').toLowerCase().replace(/[^a-z0-9]/g, '_') || 'unknown';
    const fb = EN_FALLBACK[category] || { title: `Nothing unlocked in ${category} yet`, body: 'Keep playing — scenes appear here as you see them in the story.' };
    return {
        title: L.get(`gallery.empty.${slug}.title`, fb.title),
        body:  L.get(`gallery.empty.${slug}.body`,  fb.body),
    };
}

const STORAGE_KEY = 'PnO_Gallery';

class GalleryUI {
    constructor() {
        /** @type {Set<string>} Seen image + video names (combined for the grid) */
        this._seen = new Set();

        /** @type {Set<string>} Subset of _seen — names that originated from a
         * #gif: tag, i.e. should play as video in the fullscreen viewer.
         * Names are stored bare (no extension), matching _seen. */
        this._videos = new Set();

        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {boolean} */
        this._isOpen = false;

        /** @type {string} Active category filter */
        this._activeCategory = 'All';

        /** @type {IntersectionObserver|null} Current grid observer for lazy loading */
        this._gridObserver = null;

        this._scope = events.createScope('gallery');
        this._loadFromStorage();
        this._bindEvents();
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        if (this._saveTimer) {
            clearTimeout(this._saveTimer);
            this._saveTimer = null;
            this._saveToStorage();
        }
    }

    // ── Public API ──────────────────────────────────────────────────────

    init() {
        this._root = document.getElementById('overlay-gallery');
        if (!this._root) {
            this._root = this._createContainer();
        }
    }

    open() {
        if (!this._root) this.init();
        this._isOpen = true;
        this._root.classList.remove('hidden');
        // The video manifest is guaranteed loaded by the time the player can
        // open the gallery in-game, so heal any video classification lost to the
        // construct-time migration race (foundby:F95) right before we render.
        this._reclassifyVideosFromManifest();
        this._render();
        events.emit(Events.OVERLAY_OPEN, 'overlay-gallery');
    }

    close() {
        this._isOpen = false;
        this._cleanupObserver();
        this._teardownViewer();
        // Let layout.closeOverlay handle hiding + backdrop
        events.emit(Events.OVERLAY_CLOSE, 'overlay-gallery');
    }

    /**
     * Remove any open fullscreen viewer. The viewer lives on document.body
     * (NOT inside #overlay-gallery), so hiding the overlay does NOT stop
     * its <video controls> — the one video in the app the player can
     * unmute via native controls kept playing looped audio behind the
     * hidden gallery (G key, right-click/backdrop closeTopOverlay, forced
     * events; § 91 family, audit 2026-06-07). Idempotent — safe to call
     * from close(), OVERLAY_CLOSED and FORCED_EVENT_STARTED alike.
     */
    _teardownViewer() {
        document.querySelectorAll('.gallery-viewer').forEach(v => {
            if (typeof v._pnoTeardown === 'function') {
                v._pnoTeardown();
                return;
            }
            // Fallback for a viewer without the funnel (shouldn't happen)
            v.querySelectorAll('video').forEach(el => {
                try { el.pause(); el.muted = true; } catch (e) { /* detached */ }
            });
            v.remove();
        });
    }

    /** Get total unlocked count. */
    getUnlockedCount() {
        return this._seen.size;
    }

    /** Check if a specific image has been seen. */
    hasSeen(imageName) {
        return this._seen.has(imageName);
    }

    /** Manually record an image as seen (e.g. from save data). */
    recordSeen(imageName) {
        if (!this._seen.has(imageName)) {
            this._seen.add(imageName);
            this._saveToStorage();
        }
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        // Layout-driven closes (right-click / closeTopOverlay / forced-event
        // closeAllOverlays) bypass close() — they hide #overlay-gallery
        // directly. Tear the body-level viewer down on those too, or its
        // unmuted <video controls> keeps playing behind the hidden overlay.
        this._scope.on(Events.OVERLAY_CLOSED, (overlayId) => {
            if (overlayId === 'overlay-gallery') this._teardownViewer();
        });
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this._teardownViewer());

        this._scope.on(Events.TAG_IMAGE, (imageName) => {
            this._recordImage(imageName);
        });

        this._scope.on(Events.TAG_VIDEO, (videoName) => {
            this._recordVideo(videoName);
        });

        this._scope.on(Events.LOAD_COMPLETED, () => {
            this._loadFromStorage();
        });
    }

    _recordImage(name) {
        if (!name || this._seen.has(name)) return;
        this._seen.add(name);
        this._scheduleSave();
    }

    _recordVideo(name) {
        if (!name) return;
        const newToGrid = !this._seen.has(name);
        const newToVideos = !this._videos.has(name);
        if (!newToGrid && !newToVideos) return;
        this._seen.add(name);
        this._videos.add(name);
        this._scheduleSave();
    }

    _scheduleSave() {
        if (this._saveTimer) clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => {
            this._saveTimer = null;
            this._saveToStorage();
        }, 1000);
    }

    // ── Persistence ─────────────────────────────────────────────────────

    _loadFromStorage() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            // New shape: { seen: [...], videos: [...] }
            if (parsed && Array.isArray(parsed.seen)) {
                this._seen = new Set(parsed.seen);
                this._videos = new Set(Array.isArray(parsed.videos) ? parsed.videos : []);
                // Heal a gallery whose video classification was lost: an earlier
                // migration that ran before the video manifest finished loading
                // saved videos:[] even though _seen holds real videos. Re-derive
                // (no-op + no persist until the manifest is actually ready).
                // foundby:F95 — "no videos in the gallery".
                if (this._videos.size === 0 && this._seen.size > 0) {
                    this._reclassifyVideosFromManifest();
                }
                return;
            }
            // Legacy shape: bare array of names with no img/video distinction.
            // Reclassify by consulting the appearance video manifest — names
            // that exist as `.mp4` on disk get bumped into _videos.
            if (Array.isArray(parsed)) {
                this._seen = new Set(parsed);
                this._videos = new Set();
                this._reclassifyVideosFromManifest();
            }
        } catch (e) {
            console.warn('[Gallery] Failed to load from localStorage:', e);
        }
    }

    /**
     * Re-derive _videos from _seen via the appearance video manifest, bumping
     * any seen name that exists as a `.mp4` into _videos.
     *
     * CRITICAL: bails WITHOUT persisting when the manifest isn't loaded yet.
     * The gallery singleton is constructed at module-import time, well before
     * main.js's async loadVideoManifest() resolves, so an early call sees an
     * empty manifest, classifies zero videos, and — if it persisted — bakes
     * videos:[] into localStorage permanently. That destroyed the entire Videos
     * tab for any player carrying a pre-split (bare-array) or already-poisoned
     * gallery (foundby:F95 "no videos in the gallery"). Retried from open(),
     * where the manifest is guaranteed loaded in-game.
     */
    _reclassifyVideosFromManifest() {
        let mf;
        try { mf = appearanceState?.getKnownNames?.(true); } catch (e) { return; }
        if (!mf || !mf.size) return;   // manifest not ready — don't poison; retry on open()
        let changed = false;
        for (const n of this._seen) {
            if (!this._videos.has(n) && mf.has(n)) { this._videos.add(n); changed = true; }
        }
        if (changed) this._saveToStorage();
    }

    _saveToStorage() {
        try {
            const payload = {
                seen: [...this._seen],
                videos: [...this._videos],
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch (e) {
            console.warn('[Gallery] Failed to save to localStorage:', e);
        }
    }

    // ── Rendering ───────────────────────────────────────────────────────

    _render() {
        if (!this._root) return;

        // Cleanup previous observer before re-rendering
        this._cleanupObserver();
        this._root.innerHTML = '';

        // Wrap in overlay-box for proper centering/sizing
        const box = document.createElement('div');
        box.className = 'overlay-box gallery-box';

        const frame = document.createElement('div');
        frame.className = 'gallery-frame';

        // Header
        const header = document.createElement('div');
        header.className = 'gallery-header';
        header.innerHTML = `
            <h3 class="gallery-title">Gallery</h3>
            <span class="gallery-count">${this._seen.size} unlocked</span>
        `;
        frame.appendChild(header);

        // Floating close button (top-right)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'overlay-close-float';
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', () => this.close());
        frame.appendChild(closeBtn);

        // Category tabs
        const tabs = document.createElement('div');
        tabs.className = 'gallery-tabs';

        // Get categories that have content
        const categorized = this._categorizeImages();

        const allTab = this._createTab(`All (${this._seen.size})`, this._activeCategory === 'All');
        allTab.addEventListener('click', () => {
            this._activeCategory = 'All';
            this._render();
        });
        tabs.appendChild(allTab);

        // Videos category — only render the tab when at least one video has
        // been unlocked. Sits right after All so it's findable.
        if (this._videos.size > 0) {
            const videosTab = this._createTab(
                `Videos (${this._videos.size})`,
                this._activeCategory === '__videos__'
            );
            videosTab.classList.add('gallery-tab-videos');
            videosTab.addEventListener('click', () => {
                this._activeCategory = '__videos__';
                this._render();
            });
            tabs.appendChild(videosTab);
        }

        for (const folder of IMAGE_FOLDERS) {
            if (categorized[folder] && categorized[folder].length > 0) {
                const count = categorized[folder].length;
                const tab = this._createTab(`${folder} (${count})`, this._activeCategory === folder);
                tab.addEventListener('click', () => {
                    this._activeCategory = folder;
                    this._render();
                });
                tabs.appendChild(tab);
            }
        }

        frame.appendChild(tabs);

        // Scrollable grid wrapper (acts as IntersectionObserver root)
        const gridScroll = document.createElement('div');
        gridScroll.className = 'gallery-grid-scroll';

        // Image grid
        const grid = document.createElement('div');
        grid.className = 'gallery-grid';

        let displayImages;
        if (this._activeCategory === 'All') {
            displayImages = [...this._seen];
        } else if (this._activeCategory === '__videos__') {
            displayImages = [...this._videos];
        } else {
            displayImages = categorized[this._activeCategory] || [];
        }

        if (displayImages.length === 0) {
            // Audit D17: empty-state now teases progress instead of a flat
            // "No images unlocked" line. Show a friendly icon, an evocative
            // hint matching the category, and a soft suggestion of what
            // unlocks it.
            const empty = document.createElement('div');
            empty.className = 'gallery-empty';
            const cat = this._activeCategory;
            const hint = _galleryEmptyHint(cat);
            empty.innerHTML = `
                <div class="gallery-empty-icon">\u{1F5BC}</div>
                <div class="gallery-empty-title">${hint.title}</div>
                <div class="gallery-empty-hint">${hint.body}</div>
            `;
            grid.appendChild(empty);
        } else {
            this._renderGrid(grid, gridScroll, displayImages);
        }

        gridScroll.appendChild(grid);
        frame.appendChild(gridScroll);
        box.appendChild(frame);
        this._root.appendChild(box);
    }

    /**
     * Get the group prefix for an image name (first 2 underscore-separated segments).
     * e.g. "beach_bully_paid_meeting" -> "beach_bully"
     */
    _getGroupPrefix(name) {
        const parts = name.split('_');
        if (parts.length <= 2) return parts.join('_');
        return parts.slice(0, 2).join('_');
    }

    /**
     * Format a group prefix into a readable label.
     * e.g. "beach_bully" -> "Beach Bully"
     */
    _formatGroupLabel(prefix) {
        return prefix.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    /**
     * Virtual-scroll grid: creates lightweight placeholder cells for all images,
     * then uses IntersectionObserver to lazily populate only visible ones.
     * Images are sorted by name and grouped by shared prefix with section headers.
     */
    _renderGrid(grid, scrollRoot, images) {
        // Sort images alphabetically for grouping
        const sorted = [...images].sort((a, b) => a.localeCompare(b));

        // Build grouped list with headers
        let lastPrefix = null;
        for (let i = 0; i < sorted.length; i++) {
            const prefix = this._getGroupPrefix(sorted[i]);

            // Count how many images share this prefix
            if (prefix !== lastPrefix) {
                const groupCount = sorted.filter(img => this._getGroupPrefix(img) === prefix).length;
                // Only show group header if there are 2+ images in the group
                if (groupCount >= 2) {
                    const header = document.createElement('div');
                    header.className = 'gallery-group-header';
                    header.textContent = `${this._formatGroupLabel(prefix)} [${groupCount}]`;
                    grid.appendChild(header);
                }
                lastPrefix = prefix;
            }

            const cell = document.createElement('div');
            cell.className = 'gallery-cell';
            cell.dataset.index = i;
            cell.dataset.name = sorted[i];

            // Unseen indicator
            if (!this._isSeenInGallery(sorted[i])) {
                cell.classList.add('gallery-cell-new');
            }

            grid.appendChild(cell);
        }

        // Use IntersectionObserver to lazy-load visible cells
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    const cell = entry.target;
                    if (!cell.dataset.loaded) {
                        cell.dataset.loaded = '1';
                        this._renderCell(cell, cell.dataset.name);
                        observer.unobserve(cell);
                    }
                }
            }
        }, {
            root: scrollRoot,
            rootMargin: '200px' // Pre-load 200px ahead of viewport
        });

        // Observe all placeholder cells
        grid.querySelectorAll('.gallery-cell').forEach(cell => observer.observe(cell));

        // Store observer for cleanup
        this._gridObserver = observer;
    }

    /**
     * Populate a single gallery cell with its image and label.
     * Called lazily by IntersectionObserver when the cell scrolls into view.
     */
    _renderCell(cell, name) {
        const img = document.createElement('img');
        img.className = 'gallery-thumb';
        img.loading = 'lazy';
        img.alt = name;
        const cellSrc = this._resolveImagePath(name);
        if (cellSrc) {
            img.src = cellSrc;
            img.onerror = () => {
                this._tryFolders(img, name, 0);
            };
        } else {
            // Tar-mode miss (truncated/healing archive) — placeholder, no
            // network fan-out. The cell label stays so the entry is visible.
            img.style.display = 'none';
            cell.classList.add('gallery-cell-missing');
        }

        img.addEventListener('click', () => {
            this._markSeen(name);
            cell.classList.remove('gallery-cell-new');
            this._showFullscreen(name);
        });

        cell.appendChild(img);

        // Play-icon overlay for video entries — the thumbnail is still the
        // base PNG (videos render an image fallback in the grid for perf),
        // but we badge it so users know the fullscreen view will play.
        if (this._videos.has(name)) {
            cell.classList.add('gallery-cell-video');
            const badge = document.createElement('div');
            badge.className = 'gallery-cell-play';
            badge.innerHTML = '&#9654;'; // ▶
            badge.setAttribute('aria-hidden', 'true');
            cell.appendChild(badge);
        }

        const label = document.createElement('div');
        label.className = 'gallery-cell-name';
        label.textContent = name.replace(/_/g, ' ');
        cell.appendChild(label);
    }

    /**
     * Disconnect and discard the current IntersectionObserver.
     * Called on tab switch and gallery close to avoid stale observers.
     */
    _cleanupObserver() {
        if (this._gridObserver) {
            this._gridObserver.disconnect();
            this._gridObserver = null;
        }
    }

    _createTab(label, isActive) {
        const tab = document.createElement('button');
        tab.className = 'gallery-tab' + (isActive ? ' gallery-tab-active' : '');
        tab.textContent = label;
        return tab;
    }

    _categorizeImages() {
        const result = {};
        // Simple heuristic: categorize by prefix patterns
        const prefixMap = {
            'apartment': 'Apartment', 'apt': 'Apartment', 'home': 'Apartment',
            'bar': 'Bar',
            'beach': 'Beach',
            'char': 'Characters', 'hero': 'Characters',
            'club': 'Club',
            'computer': 'Computer', 'stream': 'Computer', 'webcam': 'Computer',
            'clinic': 'Clinic', 'doctor': 'Clinic', 'mercer': 'Clinic',
            'dream': 'Dreams',
            'endgame': 'Endgame', 'ending': 'Endgame',
            'girlfriend': 'Girlfriend', 'gf': 'Girlfriend',
            'gym': 'Gym', 'trainer': 'Gym', 'malik': 'Gym',
            'item': 'Items',
            'location': 'Locations', 'street': 'Locations',
            'maid': 'Maid',
            'mall': 'Mall', 'shop': 'Mall',
            'milestone': 'Milestones',
            'office': 'Office', 'boss': 'Office',
            'park': 'Park', 'glory': 'Park',
            'photo': 'Photographer',
            'portrait': 'Portraits',
            'sms': 'SMS',
            'salon': 'Salon',
            'sugar': 'SugarDaddy', 'daddy': 'SugarDaddy',
        };

        for (const name of this._seen) {
            let folder = 'System'; // default
            const lower = name.toLowerCase();
            for (const [prefix, cat] of Object.entries(prefixMap)) {
                if (lower.startsWith(prefix)) {
                    folder = cat;
                    break;
                }
            }
            if (!result[folder]) result[folder] = [];
            result[folder].push(name);
        }

        return result;
    }

    // ── Seen/Unseen Tracking ─────────────────────────────────────────────

    _isSeenInGallery(name) {
        try {
            const seen = JSON.parse(localStorage.getItem('PnO_GallerySeen') || '[]');
            return seen.includes(name);
        } catch(e) { return false; }
    }

    _markSeen(name) {
        try {
            const seen = JSON.parse(localStorage.getItem('PnO_GallerySeen') || '[]');
            if (!seen.includes(name)) {
                seen.push(name);
                localStorage.setItem('PnO_GallerySeen', JSON.stringify(seen));
            }
        } catch(e) {}
    }

    // ── Fullscreen Viewer ───────────────────────────────────────────────

    /**
     * For a base name, scan the appearance manifests for which variant
     * axes (chastity / longhair / boobs / masc) have at least one variant
     * on disk. Returns { chastity, longhair, boobs, masc }.
     * Used to decide which checkbox toggles to surface in the viewer.
     * The suffix tail must FULLMATCH the ordered-axis grammar (parsed via
     * appearance.js parseVariantTail) — the old substring checks counted
     * sibling-prefix names (e.g. `foo_climax_chastity` for base `foo`)
     * and surfaced no-op checkboxes.
     */
    _availableVariantAxes(baseName, isVideo) {
        const known = appearanceState.getKnownNames(isVideo);
        let availMask = 0;
        for (const candidate of known) {
            if (candidate === baseName) continue;
            if (!candidate.startsWith(baseName + '_')) continue;
            const tailMask = appearanceState.parseVariantTail(candidate.slice(baseName.length));
            if (tailMask > 0) availMask |= tailMask;
        }
        // Bit order matches appearance.js: 0=chastity, 1=longhair, 2=boobs, 3=masc.
        return {
            chastity: !!(availMask & 1),
            longhair: !!(availMask & 2),
            boobs:    !!(availMask & 4),
            masc:     !!(availMask & 8),
        };
    }

    /**
     * Resolve the most-specific variant name that exists for the given
     * mask, walking submasks if the exact combination isn't on disk.
     * Same ladder as appearance.js resolveVariantName (4 axes, masc highest
     * bit + masc ⊥ boobs), candidates built via the shared name-builder.
     */
    _resolveVariantWithMask(baseName, mask, isVideo) {
        const known = appearanceState.getKnownNames(isVideo);
        let activeMask = 0;
        if (mask.chastity) activeMask |= 1;
        if (mask.longhair) activeMask |= 2;
        if (mask.boobs)    activeMask |= 4;
        if (mask.masc)     activeMask |= 8;
        // male ⊥ boobs: drop the boobs bit whenever masc is set (mirrors
        // appearance.js _buildActiveMask).
        if (activeMask & 8) activeMask &= ~4;
        if (activeMask === 0) return baseName;
        for (let m = activeMask; m > 0; m = (m - 1) & activeMask) {
            const candidate = appearanceState.buildVariantName(baseName, m);
            if (known.has(candidate)) return candidate;
        }
        return baseName; // base fallback
    }

    _showFullscreen(name) {
        // Build ordered list of visible images for prev/next navigation
        const visibleImages = [...(this._root?.querySelectorAll('.gallery-cell[data-name]') || [])]
            .map(el => el.dataset.name).filter(Boolean);
        let currentIdx = visibleImages.indexOf(name);

        const viewer = document.createElement('div');
        viewer.className = 'gallery-viewer';

        const mediaContainer = document.createElement('div');
        mediaContainer.className = 'gallery-viewer-media-wrap';

        // Variant toggles — chastity / longhair / boobs. Default to the
        // player's CURRENT in-game appearance state (so re-opening the
        // viewer matches what they last saw on screen). Only axes with
        // at least one variant on disk get a checkbox; others stay off.
        const variantState = (() => {
            // Fresh-read mask from appearance.js — _activeStates misses
            // story-written flags (spa breast growth, salon hair) and goes
            // stale across save reloads (BUG_PATTERNS §92; the fix landed in
            // _buildActiveMask, so derive ALL four defaults from its bits).
            // Bit order: 0=chastity, 1=longhair, 2=boobs, 3=masc — masc⊥boobs
            // is already applied inside the mask builder.
            let mask = 0;
            try { mask = appearanceState?.getActiveMask?.() || 0; } catch (e) {}
            return {
                chastity: !!(mask & 1),
                longhair: !!(mask & 2),
                boobs:    !!(mask & 4),
                masc:     !!(mask & 8),
            };
        })();

        // The fullscreen viewer always works on the BASE name regardless
        // of which variant the cell records — the cell's name already
        // strips variants implicitly (only base names enter _seen via
        // Ink #img: / #gif: emissions). For safety we still trim variant
        // suffixes from `n` (appearance.js stripVariantSuffix) before
        // scanning axes.
        const renderMedia = (n) => {
            // Fully release the OUTGOING <video> before innerHTML detaches it.
            // Prev/Next/arrow nav re-renders into the same container; a bare
            // innerHTML='' detaches the old clip without pausing, muting, or
            // freeing its decoded buffer (same class the story-content
            // _releaseVideos fix covers). On §91 browsers (FF/Safari) a detached
            // <video> keeps its audio looping if the player unmuted via native
            // controls, and the buffer lingers until GC — scrubbing a long
            // Videos category piles them up on mobile. pause+mute+src-clear+load
            // is the canonical stop-and-release.
            mediaContainer.querySelectorAll('video').forEach(v => {
                try {
                    v.pause();
                    v.muted = true;
                    v.removeAttribute('src');
                    v.load();          // aborts resource selection, frees the buffer
                } catch (e) { /* detached mid-flight */ }
            });
            // § 106: frame-hosted clips — removing the frame destroys its
            // document and frees the decoded-frame pool that the unload
            // above provably does NOT free for main-document videos.
            releaseClipsIn(mediaContainer, 'iframe.gallery-viewer-media');
            mediaContainer.innerHTML = '';
            const baseName = appearanceState.stripVariantSuffix(n);
            const isVideo = this._videos.has(baseName) || this._videos.has(n);
            const resolvedName = this._resolveVariantWithMask(baseName, variantState, isVideo);

            if (isVideo) {
                // § 106: silent clips play inside a throwaway playback frame;
                // voice clips stay inline (their §91 audio surfaces sweep
                // main-document videos). Frame side effect: clicks on the
                // clip/controls no longer bubble to the viewer's close-on-
                // click handling — native controls become actually usable.
                const { host: _gHost, video, frame: _gFrame } = createIsolatedClip(
                    { hostClassName: 'gallery-viewer-media video-fade-in', forceInline: isVoiceClip(resolvedName) },
                    (h) => mediaContainer.appendChild(h),
                );
                const _gSrc = `assets/videos/${resolvedName}.mp4`;
                video.src = _gFrame ? new URL(_gSrc, document.baseURI).href : _gSrc;
                video.autoplay = true;
                video.loop = true;
                video.muted = true;                         // iOS/Safari autoplay gate
                // Respect the SFX slider if the player unmutes via the
                // native controls — same rule as the story-content videos.
                try { video.volume = audioManager.sfxVolume; } catch (e) { /* keep default */ }
                video.playsInline = true;
                video.setAttribute('playsinline', '');
                video.controls = true;
                video.preload = 'metadata';
                // Robust reveal (mobile `loadeddata` may never fire under
                // metadata preload + suppressed autoplay) — see story-content
                // _addVideo. Reveal on loadedmetadata + timeout, kick play().
                const _revealG = () => _gHost.classList.add('is-loaded');
                video.addEventListener('loadedmetadata', _revealG, { once: true });
                video.addEventListener('loadeddata', _revealG, { once: true });
                setTimeout(() => { if (_gHost.isConnected) _revealG(); }, 1500);
                // Edge-403 retry, same recipe as story-content _addVideo: the
                // itch BunnyCDN edge intermittently drops a request that
                // succeeds on a second hit. One load() re-issue, then give up.
                let _galVideoRetried = false;
                video.onerror = () => {
                    if (!_galVideoRetried) {
                        _galVideoRetried = true;
                        setTimeout(() => {
                            try { video.load(); } catch (e) { /* give up silently */ }
                        }, 600);
                        return;
                    }
                    console.warn(`[Gallery] Video failed after retry: ${resolvedName}`);
                };
                try { const _p = video.play(); if (_p) _p.catch(() => {}); } catch (e) { /* controls fallback */ }
                // (host already appended by createIsolatedClip)
            } else {
                const img = document.createElement('img');
                img.className = 'gallery-viewer-media';
                img.alt = resolvedName;
                const viewerSrc = this._resolveImagePath(resolvedName);
                if (viewerSrc) {
                    img.src = viewerSrc;
                    img.onerror = () => this._tryFolders(img, resolvedName, 0);
                } else {
                    img.alt = `${resolvedName} (unavailable)`;
                    img.classList.add('gallery-viewer-missing');
                }
                mediaContainer.appendChild(img);
            }

            // Variant toggles row (only if at least one axis has variants).
            const axes = this._availableVariantAxes(baseName, isVideo);
            if (axes.chastity || axes.longhair || axes.boobs || axes.masc) {
                const toggles = document.createElement('div');
                toggles.className = 'gallery-viewer-variant-toggles';
                const addToggle = (key, label, available) => {
                    const wrap = document.createElement('label');
                    wrap.className = 'gallery-viewer-variant-toggle' + (available ? '' : ' is-disabled');
                    const cb = document.createElement('input');
                    cb.type = 'checkbox';
                    cb.checked = !!variantState[key];
                    cb.disabled = !available;
                    cb.addEventListener('change', (e) => {
                        e.stopPropagation();
                        variantState[key] = cb.checked;
                        // masc ⊥ boobs: ticking one clears the other so the
                        // toggle row never shows a contradictory combination.
                        if (cb.checked && key === 'masc') variantState.boobs = false;
                        if (cb.checked && key === 'boobs') variantState.masc = false;
                        renderMedia(n);
                    });
                    wrap.appendChild(cb);
                    const span = document.createElement('span');
                    span.textContent = label;
                    wrap.appendChild(span);
                    toggles.appendChild(wrap);
                };
                addToggle('chastity', 'Chastity', axes.chastity);
                addToggle('longhair', 'Long Hair', axes.longhair);
                addToggle('boobs',    'Breasts',   axes.boobs);
                addToggle('masc',     'Masculine', axes.masc);
                mediaContainer.appendChild(toggles);
            }

            // Image name label — show resolved variant name + base if differs
            const label = document.createElement('div');
            label.className = 'gallery-viewer-label';
            const variantSuffix = resolvedName !== baseName ? ` · ${resolvedName.slice(baseName.length + 1)}` : '';
            label.textContent = `${baseName}${variantSuffix} (${currentIdx + 1}/${visibleImages.length})`;
            mediaContainer.appendChild(label);
        };

        viewer.appendChild(mediaContainer);

        // Prev/Next buttons
        if (visibleImages.length > 1) {
            const prevBtn = document.createElement('button');
            prevBtn.className = 'gallery-viewer-nav gallery-viewer-prev';
            prevBtn.innerHTML = '&#8249;';
            prevBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                currentIdx = (currentIdx - 1 + visibleImages.length) % visibleImages.length;
                renderMedia(visibleImages[currentIdx]);
            });
            viewer.appendChild(prevBtn);

            const nextBtn = document.createElement('button');
            nextBtn.className = 'gallery-viewer-nav gallery-viewer-next';
            nextBtn.innerHTML = '&#8250;';
            nextBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                currentIdx = (currentIdx + 1) % visibleImages.length;
                renderMedia(visibleImages[currentIdx]);
            });
            viewer.appendChild(nextBtn);
        }

        // Single teardown funnel for every close path (X, ESC, backdrop,
        // AND the gallery-level _teardownViewer for layout-driven closes).
        // Pause+mute before remove — the player can unmute this video via
        // its native controls, and a hide-only close left it audible.
        const teardown = () => {
            viewer.querySelectorAll('video').forEach(el => {
                try { el.pause(); el.muted = true; } catch (e) { /* detached */ }
            });
            viewer.remove();
            document.removeEventListener('keydown', keyHandler);
        };
        viewer._pnoTeardown = teardown;

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'gallery-viewer-close';
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            teardown();
        });
        viewer.appendChild(closeBtn);

        // Keyboard navigation
        const keyHandler = (e) => {
            if (e.key === 'Escape') { teardown(); }
            if (e.key === 'ArrowLeft' && visibleImages.length > 1) {
                currentIdx = (currentIdx - 1 + visibleImages.length) % visibleImages.length;
                renderMedia(visibleImages[currentIdx]);
            }
            if (e.key === 'ArrowRight' && visibleImages.length > 1) {
                currentIdx = (currentIdx + 1) % visibleImages.length;
                renderMedia(visibleImages[currentIdx]);
            }
        };
        document.addEventListener('keydown', keyHandler);

        // Click backdrop to close
        viewer.addEventListener('click', (e) => {
            if (e.target === viewer) { teardown(); }
        });

        document.body.appendChild(viewer);
        // Initial render AFTER the viewer is connected: the § 106 playback
        // frame only gets a document once its iframe is in the DOM (a
        // detached-render would silently fall back to the leaking inline
        // path). Prev/Next/arrow re-renders were always post-mount.
        renderMedia(name);
    }

    // ── Image Path Resolution ───────────────────────────────────────────

    _resolveImagePath(name) {
        // Tar mode (Electron/Capacitor/itch bundled tar): the tar archive
        // owns every image as an extracted blob URL — direct `assets/images/`
        // paths don't exist on disk. Try the blob first; fall back to file
        // URLs only when not in tar mode or the blob isn't indexed yet.
        if (isTarMode()) {
            const blob = getImageUrl(name);
            if (blob) return blob;
            // Tar mode: loose `assets/images/` paths don't exist in the
            // build — falling through used to fan out ~73 dead CDN requests
            // per missing cell via _tryFolders (request storm whenever the
            // tar is truncated/healing). Null = missing; callers render the
            // placeholder state instead.
            return null;
        }

        const manifest = getImageManifest();
        const cache = getImageCache();

        // 1. Check in-memory cache (populated by story-content.js)
        if (cache && cache[name]) {
            return `assets/images/${cache[name]}/${name}.webp`;
        }

        // 2. Check manifest (eliminates 404 fallback chains)
        if (manifest && manifest[name]) {
            // Cache for future lookups
            if (cache) cache[name] = manifest[name];
            return `assets/images/${manifest[name]}/${name}.webp`;
        }

        // 3. Fallback: first folder (onerror will try others)
        return `assets/images/${IMAGE_FOLDERS[0]}/${name}.webp`;
    }

    _tryFolders(img, name, index) {
        const src = img.src;
        // Try webp → png fallback within each folder before moving to next
        if (src.endsWith('.webp')) {
            img.src = src.replace(/\.webp$/, '.png');
            return;
        }
        // .png failed — move to next folder (start with webp)
        index++;
        if (index < IMAGE_FOLDERS.length) {
            img.src = `assets/images/${IMAGE_FOLDERS[index]}/${name}.webp`;
            img.onerror = () => this._tryFolders(img, name, index);
        } else {
            // Try jpg as last resort
            img.src = `assets/images/${IMAGE_FOLDERS[0]}/${name}.jpg`;
            img.onerror = () => {
                img.style.display = 'none';
            };
        }
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'overlay-gallery';
        el.className = 'overlay-container hidden';
        document.body.appendChild(el);
        return el;
    }

    // ── Serialization ───────────────────────────────────────────────────

    serialize() {
        return [...this._seen];
    }

    deserialize(data) {
        if (Array.isArray(data)) {
            for (const name of data) this._seen.add(name);
            this._saveToStorage();
        }
    }
}

/** @type {GalleryUI} */
export const galleryUI = new GalleryUI();
